import type {
  EmailBuilderState,
  NewMemberItem,
  NewMembersSettings,
} from "./emailBuilderData";
import type { EmailAudienceMemberOption } from "./emailMockData";

// D22 / design (16): new blocks and the "Add New Members" picker use a 60-day
// window. Blocks saved before that have no `days` and keep the 30-day window
// of CTO decision C3 (their title names it). The backend send refresh
// (cmpNewsletterNewMembersRefresh.ts) reads the same `days` field.
export const NEW_MEMBERS_WINDOW_DAYS = 60;
export const LEGACY_NEW_MEMBERS_WINDOW_DAYS = 30;

export const getNewMembersWindowDays = (
  settings: Pick<NewMembersSettings, "days">,
): number =>
  typeof settings.days === "number" && settings.days > 0
    ? settings.days
    : LEGACY_NEW_MEMBERS_WINDOW_DAYS;
const dayMilliseconds = 86400000;

type RecentNewMembersInput = {
  members: readonly EmailAudienceMemberOption[];
  days?: number;
  now?: number;
};

const isActiveMember = (member: EmailAudienceMemberOption) =>
  member.lifecycleStatus.toLowerCase() === "active";

const toNewMemberItem = (member: EmailAudienceMemberOption): NewMemberItem => ({
  id: member.id,
  name: member.companyName,
  logoUrl: member.companyLogo || "",
  category: member.categoryLabels?.join(", ") || "",
  joinedAtUtcMilli: member.dateJoinedUtcMilli ?? null,
  linkUrl: "",
});

// Active members whose joined date is within the window, sorted by name.
export const getRecentNewMembers = ({
  members,
  days = NEW_MEMBERS_WINDOW_DAYS,
  now = Date.now(),
}: RecentNewMembersInput): NewMemberItem[] =>
  members
    .filter(
      (member) =>
        isActiveMember(member) &&
        typeof member.dateJoinedUtcMilli === "number" &&
        member.dateJoinedUtcMilli <= now &&
        member.dateJoinedUtcMilli >= now - days * dayMilliseconds,
    )
    .map(toNewMemberItem)
    .sort((a, b) => a.name.localeCompare(b.name));

// Design (16) "Add New Members" lists the Active members who joined within
// the block's window, newest joined date first.
export const getNewMembersPickerCandidates = ({
  members,
  days = NEW_MEMBERS_WINDOW_DAYS,
  now = Date.now(),
}: {
  members: readonly EmailAudienceMemberOption[];
  days?: number;
  now?: number;
}): NewMemberItem[] =>
  getRecentNewMembers({ members, days, now })
    .sort(
      (a, b) =>
        (b.joinedAtUtcMilli ?? -Infinity) - (a.joinedAtUtcMilli ?? -Infinity) ||
        a.name.localeCompare(b.name),
    );

type RefreshNewMembersItemsInput = {
  settings: NewMembersSettings;
  members: readonly EmailAudienceMemberOption[];
  now?: number;
};

const sameItems = (left: NewMemberItem[], right: NewMemberItem[]) =>
  left.length === right.length &&
  left.every((item, index) => {
    const other = right[index];
    return (
      item.id === other.id &&
      item.name === other.name &&
      item.logoUrl === other.logoUrl &&
      item.category === other.category &&
      item.joinedAtUtcMilli === other.joinedAtUtcMilli &&
      item.linkUrl === other.linkUrl
    );
  });

// The block copies its member list when it is added. Refresh it from the
// already-loaded member list: include every member who qualifies now and
// every Active member the user added, except members the user removed, and
// keep a saved item's link. Returns null when nothing changed so callers do
// not mark the form dirty.
export const refreshNewMembersItems = ({
  settings,
  members,
  now = Date.now(),
}: RefreshNewMembersItemsInput): NewMemberItem[] | null => {
  const excluded = new Set(settings.excludedMemberIds || []);
  const included = new Set(settings.includedMemberIds || []);
  const savedById = new Map(settings.items.map((item) => [item.id, item]));
  const recent = getRecentNewMembers({
    members,
    days: getNewMembersWindowDays(settings),
    now,
  });
  const recentIds = new Set(recent.map((member) => member.id));
  const added = members
    .filter(
      (member) =>
        included.has(member.id) &&
        !recentIds.has(member.id) &&
        isActiveMember(member),
    )
    .map(toNewMemberItem);
  const next = [...recent, ...added]
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((member) => !excluded.has(member.id))
    .map((member) => ({
      ...member,
      linkUrl: savedById.get(member.id)?.linkUrl || member.linkUrl,
    }));
  const current = [...settings.items].sort((a, b) => a.name.localeCompare(b.name));
  return sameItems(current, next) ? null : next;
};

type RefreshNewMembersBlocksInput = {
  builderState: EmailBuilderState;
  members: readonly EmailAudienceMemberOption[];
  /** Skip the refresh while the member list is loading or failed. */
  membersReady: boolean;
  now?: number;
};

// Shared by the builder panel and the send, schedule, test and preview flows.
// Returns the same object when nothing changed.
export const refreshNewMembersBlocks = ({
  builderState,
  members,
  membersReady,
  now = Date.now(),
}: RefreshNewMembersBlocksInput): EmailBuilderState => {
  if (!membersReady || members.length === 0) return builderState;
  let changed = false;
  const blocks = builderState.blocks.map((block) => {
    if (block.type !== "newMembers") return block;
    const items = refreshNewMembersItems({ settings: block.settings, members, now });
    if (!items) return block;
    changed = true;
    return { ...block, settings: { ...block.settings, items } };
  });
  return changed ? { ...builderState, blocks } : builderState;
};
